import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DashboardService } from "../../services/dashboard.service";
import equal from "fast-deep-equal";
import {
  FormBuilder,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Subject } from "rxjs";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { CommonModule } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { CalenderDirective } from "../../directive/calender.directive";

@Component({
  selector: "app-filter-popup",
  templateUrl: "./filter-popup.component.html",
  styleUrls: ["./filter-popup.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    CalenderDirective,
  ],
})
export class FilterPopupComponent implements OnInit, OnDestroy {
  filterData: any;
  filterSelected: any;
  lastAppliedFilter: any;
  initialFilterValues: any;

  // main reactive form for the dialog
  filterForm!: FormGroup;
  ComparisonNormalizer = ComparisonNormalizer;

  public filteredItemList: string[] = [];
  public value: string = "";
  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<FilterPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dashboardService: DashboardService,
    private readonly fb: FormBuilder,
  ) {
    this.filterData = data;
    this.filterForm = this.fb.group({
      program: [[]],
      transcripts: ["All"],
      startDate: this.fb.group({ startDate: [null], endDate: [null] }),
      assignedDate: this.fb.group({ startDate: [null], endDate: [null] }),
    });
  }

  ngOnInit(): void {
    this.filterSelected = this.dashboardService.getCurrentFilter();

    // Ensure program is always an array
    if (!Array.isArray(this.filterSelected.program)) {
      this.filterSelected.program = this.filterSelected.program
        ? [this.filterSelected.program]
        : [];
    }

    this.filteredItemList = [...this.filterData.program];

    // Build main form (including startDate group for validation)
    this.buildFilterForm();

    // snapshot of initial form values for change detection
    this.initialFilterValues = this.filterForm.getRawValue();

    this.lastAppliedFilter = { ...this.filterSelected };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Build the reactive form used by the dialog, including
   * validation for Program Start Date.
   */
  private buildFilterForm(): void {
    this.filterForm = this.fb.group({
      program: [this.filterSelected.program ?? []],
      transcripts: [this.filterSelected.transcripts ?? "All"],
      startDate: this.fb.group(
        {
          startDate: [this.filterSelected.startDate?.startDate || null],
          endDate: [this.filterSelected.startDate?.endDate || null],
        },
        {
          // custom validator to enforce valid date range
          validators: this.dateRangeValidator,
        },
      ),
      assignedDate: this.fb.group({
        startDate: [this.filterSelected.assignedDate?.startDate || null],
        endDate: [this.filterSelected.assignedDate?.endDate || null],
      }),
    });
  }

  /**
   * Validator to ensure:
   *  - if both start and end exist, end >= start
   *  - if only one is present, the group can still be considered valid
   *    (adjust if you want both required).
   */
  private dateRangeValidator(group: AbstractControl): ValidationErrors | null {
    const start = group.get("startDate")?.value as Date | null;
    const end = group.get("endDate")?.value as Date | null;

    if (!start && !end) {
      // no selection – treat as valid; change if you want this required
      return null;
    }

    if (start && end && end < start) {
      return { dateRangeInvalid: true };
    }

    return null;
  }

  public onFilterChange(search: string) {
    const currentValue = this.filterForm.get("program")?.value;
    const selected = Array.isArray(currentValue)
      ? currentValue.filter((i) => ComparisonNormalizer.string(i) !== "all")
      : [];

    this.filteredItemList = this.filterData.program.filter((item) =>
      item.toLowerCase().includes(search.toLowerCase()),
    );

    if (this.filteredItemList?.length === 0) {
      this.filteredItemList = [];
    } else {
      // add selected items back so they don’t disappear
      this.filteredItemList = [
        ...new Set([...this.filteredItemList, ...selected]),
      ];
      const programControl = this.filterForm.get("program");
      const currentProgram = Array.isArray(programControl?.value)
        ? programControl!.value
        : [];

      if (
        currentProgram.some(
          (item: string) => ComparisonNormalizer.string(item) === "all",
        )
      ) {
        const filtered = currentProgram.filter(
          (item: string) => ComparisonNormalizer.string(item) !== "all",
        );
        programControl?.setValue(filtered, { emitEvent: false });
      }
    }
  }

  public clearFilter() {
    this.value = "";

    // remove 'All' if present
    const programControl = this.filterForm.get("program");
    const currentValue = programControl?.value;
    const selected = Array.isArray(currentValue)
      ? currentValue.filter((i) => ComparisonNormalizer.string(i) !== "all")
      : [];

    programControl?.setValue(selected, { emitEvent: false });

    // reset list
    this.filteredItemList = [...this.filterData.program];
  }

  public toggleSelectAll() {
    const programControl = this.filterForm.get("program");
    if (!programControl) {
      return;
    }

    if (this.isAllSelected()) {
      programControl.setValue([], { emitEvent: false });
    } else {
      programControl.setValue(this.filteredItemList, { emitEvent: false });
    }
  }

  public isAllSelected(): boolean {
    const selected = this.programSelection;
    const list = this.filteredItemList.length
      ? this.filteredItemList
      : this.filterData.program;

    if (!list.length) {
      return false;
    }

    return list.every((item) => selected.includes(item));
  }

  public itemClicked() {
    const programControl = this.filterForm.get("program");
    if (!programControl) {
      return;
    }

    const currentValue = programControl.value;
    if (
      Array.isArray(currentValue) &&
      currentValue.some(
        (item: string) => ComparisonNormalizer.string(item) === "all",
      )
    ) {
      const filtered = currentValue.filter(
        (item) => ComparisonNormalizer.string(item) !== "all",
      );
      programControl.setValue(filtered, { emitEvent: false });
    }
  }

  onCancel(): void {
    this.filterSelected = { ...this.lastAppliedFilter };
    this.dashboardService.updateFilter(this.filterSelected);
    this.dialogRef.close({ action: "cancel", data: this.filterSelected });
  }

  onApply(): void {
    // do not apply if form invalid; mark all as touched to show errors
    if (this.filterForm.invalid) {
      this.filterForm.markAllAsTouched();
      return;
    }

    const formValue = this.filterForm.getRawValue();

    this.filterSelected = {
      ...this.filterSelected,
      program: formValue.program,
      transcripts: formValue.transcripts,
      startDate: { ...formValue.startDate },
      assignedDate: { ...formValue.assignedDate },
    };

    this.lastAppliedFilter = { ...this.filterSelected };
    this.dashboardService.updateFilter(this.filterSelected);
    this.dialogRef.close({ action: "apply", data: this.filterSelected });
  }

  onDateRangeChanged(
    range: { startDate: Date; endDate: Date },
    type: "startDate" | "assignedDate",
  ): void {
    this.filterSelected[type].startDate = range.startDate;
    this.filterSelected[type].endDate = range.endDate;

    const group = this.filterForm.get(type);
    if (group) {
      group.patchValue(
        {
          startDate: range.startDate,
          endDate: range.endDate,
        },
        { emitEvent: false },
      );
      group.updateValueAndValidity();
    }
  }

  getFormattedRange(type: "startDate" | "assignedDate"): string {
    const { startDate, endDate } = this.filterSelected[type];
    if (!startDate) return "";
    const start = this.formatDate(startDate);
    if (!endDate) return start;
    return `${start} - ${this.formatDate(endDate)}`;
  }

  formatDate(date: Date): string {
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}/${date.getFullYear()}`;
  }

  onResetAll() {
    this.filterSelected = {
      program: [],
      status: "All",
      tloStatus: "All",
      transcripts: "All",
      startDate: {
        startDate: null,
        endDate: null,
      },
      assignedDate: {
        startDate: null,
        endDate: null,
      },
    };

    this.filteredItemList = [...this.filterData.program];

    // reset reactive form as well
    this.filterForm.reset({
      program: [],
      transcripts: "All",
      startDate: {
        startDate: null,
        endDate: null,
      },
      assignedDate: {
        startDate: null,
        endDate: null,
      },
    });

    this.dashboardService.updateFilter(this.filterSelected);
  }

  hasActiveFilters(): boolean {
    if (!this.filterForm) {
      return false;
    }

    const value = this.filterForm.getRawValue();

    const hasProgram = Array.isArray(value.program) && value.program.length > 0;

    const hasTranscripts =
      value.transcripts &&
      ComparisonNormalizer.string(value.transcripts) !== "all";

    const hasStartDate =
      (value.startDate?.startDate ?? null) !== null ||
      (value.startDate?.endDate ?? null) !== null;

    const hasAssignedDate =
      (value.assignedDate?.startDate ?? null) !== null ||
      (value.assignedDate?.endDate ?? null) !== null;

    // tloStatus is still not part of the reactive form, so we keep using filterSelected
    const hasTloStatus =
      this.filterSelected?.tloStatus &&
      ComparisonNormalizer.string(this.filterSelected.tloStatus) !== "all";

    return (
      hasProgram ||
      hasTranscripts ||
      hasTloStatus ||
      hasStartDate ||
      hasAssignedDate
    );
  }

  hasChanges(): boolean {
    if (!this.filterForm) {
      return false;
    }

    const currentValue = this.filterForm.getRawValue();
    return !equal(currentValue, this.initialFilterValues);
  }

  get programSelection(): string[] {
    return this.filterForm?.get("program")?.value || [];
  }
}
